import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  createMapPoi,
  deleteMapPoi,
  getMapPois,
  updateMapPoi,
} from "../../api/client.js";

import {
  ConfirmDialog,
  Dialog,
} from "../Dialog.jsx";
import { Icon } from "../Icon.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import { getNextTableSort, useTableSort } from "../../hooks/useTableSort.js";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterPagination,
  DataMasterRowActions,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

const EMPTY_FORM = {
  poiCode: "",
  poiName: "",
  poiCategory: "HOTEL",
  addressText: "",
  latitude: "",
  longitude: "",
  iconKey: "",
  markerColor: "",
  contactName: "",
  contactPhone: "",
  openingHours: "",
  notes: "",
  showOnMap: true,
  canBeWaypoint: false,
  priority: 0,
  active: true,
};

function cleanCoordinate(value) {
  const text = String(value ?? "").trim();
  return text ? text.replace(",", ".") : "";
}

function formatCoordinate(latitude, longitude) {
  if (latitude === null || latitude === undefined || longitude === null || longitude === undefined) {
    return "—";
  }

  const lat = Number(latitude);
  const lng = Number(longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return "—";
  }

  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function formatTimestamp(value) {
  if (!value) return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function ActiveBadge({ active }) {
  return (
    <span className={`master-active master-active--${active ? "yes" : "no"}`}>
      {active ? "Aktif" : "Nonaktif"}
    </span>
  );
}

function BooleanBadge({ enabled, yes = "Ya", no = "Tidak" }) {
  return (
    <span className={`master-active master-active--${enabled ? "yes" : "no"}`}>
      {enabled ? yes : no}
    </span>
  );
}

function mapPoiToForm(poi) {
  return {
    ...EMPTY_FORM,
    ...poi,
    latitude:
      poi.latitude === null || poi.latitude === undefined
        ? ""
        : String(poi.latitude),
    longitude:
      poi.longitude === null || poi.longitude === undefined
        ? ""
        : String(poi.longitude),
    priority: Number(poi.priority || 0),
    showOnMap: poi.showOnMap !== false,
    canBeWaypoint: poi.canBeWaypoint === true,
    active: poi.active !== false,
  };
}

function buildPayload(form) {
  return {
    poiCode: form.poiCode,
    poiName: form.poiName,
    poiCategory: form.poiCategory,
    addressText: form.addressText,
    latitude: cleanCoordinate(form.latitude),
    longitude: cleanCoordinate(form.longitude),
    iconKey: form.iconKey,
    markerColor: form.markerColor,
    contactName: form.contactName,
    contactPhone: form.contactPhone,
    openingHours: form.openingHours,
    notes: form.notes,
    showOnMap: form.showOnMap === true,
    canBeWaypoint: form.canBeWaypoint === true,
    priority: Number(form.priority || 0),
    active: form.active === true,
  };
}

function validateForm(form) {
  if (!String(form.poiCode || "").trim()) return "Kode POI wajib diisi.";
  if (!String(form.poiName || "").trim()) return "Nama POI wajib diisi.";
  if (!String(form.poiCategory || "").trim()) return "Kategori POI wajib dipilih.";

  const latitude = cleanCoordinate(form.latitude);
  const longitude = cleanCoordinate(form.longitude);

  if (Boolean(latitude) !== Boolean(longitude)) {
    return "Latitude dan longitude wajib diisi bersamaan.";
  }

  if (latitude) {
    const lat = Number(latitude);
    const lng = Number(longitude);

    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      return "Latitude harus berupa angka antara -90 sampai 90.";
    }

    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      return "Longitude harus berupa angka antara -180 sampai 180.";
    }
  }

  return "";
}

function PoiForm({
  busy,
  categories,
  initial,
  onCancel,
  onSubmit,
  title,
}) {
  const categoryByCode = useMemo(
    () =>
      new Map(
        categories.map((category) => [
          category.value,
          category,
        ]),
      ),
    [categories],
  );
  const [form, setForm] = useState(() => {
    const merged = {
      ...EMPTY_FORM,
      ...initial,
    };
    const category = categoryByCode.get(merged.poiCategory);

    return {
      ...merged,
      iconKey: merged.iconKey || category?.iconKey || "",
      markerColor: merged.markerColor || category?.markerColor || "#475569",
    };
  });
  const [localError, setLocalError] = useState("");

  function updateField(name, value) {
    setForm((current) => {
      const next = {
        ...current,
        [name]: value,
      };

      if (name === "poiCategory") {
        const category = categoryByCode.get(value);
        next.iconKey = current.iconKey || category?.iconKey || "";
        next.markerColor = category?.markerColor || current.markerColor;
      }

      return next;
    });
  }

  function submit(event) {
    event.preventDefault();

    const validationError = validateForm(form);

    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError("");
    onSubmit(buildPayload(form));
  }

  return (
    <Dialog onClose={onCancel} title={title}>
      <form onSubmit={submit}>
        <div className="dialog-card__body grouped-master-form poi-dialog-body">
          {localError && <div className="error-banner">{localError}</div>}

          <section className="master-form-group">
            <h3>Identitas POI</h3>
            <div className="dialog-form__grid aligned-form-grid">
              <label>
                <span>Kode</span>
                <input
                  onChange={(event) => updateField("poiCode", event.target.value)}
                  required
                  value={form.poiCode}
                />
              </label>

              <label>
                <span>Nama POI</span>
                <input
                  onChange={(event) => updateField("poiName", event.target.value)}
                  required
                  value={form.poiName}
                />
              </label>

              <label>
                <span>Kategori</span>
                <select
                  onChange={(event) => updateField("poiCategory", event.target.value)}
                  required
                  value={form.poiCategory}
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Prioritas</span>
                <input
                  onChange={(event) => updateField("priority", event.target.value)}
                  type="number"
                  value={form.priority}
                />
              </label>
            </div>
          </section>

          <section className="master-form-group">
            <h3>Lokasi</h3>
            <div className="dialog-form__grid aligned-form-grid">
              <label className="dialog-form__field--wide">
                <span>Alamat</span>
                <input
                  onChange={(event) => updateField("addressText", event.target.value)}
                  value={form.addressText}
                />
              </label>

              <label>
                <span>Latitude</span>
                <input
                  onChange={(event) => updateField("latitude", event.target.value)}
                  placeholder="-6.966000"
                  value={form.latitude}
                />
              </label>

              <label>
                <span>Longitude</span>
                <input
                  onChange={(event) => updateField("longitude", event.target.value)}
                  placeholder="110.416000"
                  value={form.longitude}
                />
              </label>
            </div>
          </section>

          <section className="master-form-group">
            <h3>Tampilan Peta</h3>
            <div className="dialog-form__grid aligned-form-grid">
              <label>
                <span>Icon</span>
                <input
                  onChange={(event) => updateField("iconKey", event.target.value)}
                  value={form.iconKey}
                />
              </label>

              <label>
                <span>Warna Marker</span>
                <input
                  onChange={(event) => updateField("markerColor", event.target.value)}
                  type="color"
                  value={form.markerColor || "#475569"}
                />
              </label>

              <label className="checkbox-field">
                <input
                  checked={form.showOnMap}
                  onChange={(event) => updateField("showOnMap", event.target.checked)}
                  type="checkbox"
                />
                <span>Tampil di Peta</span>
              </label>

              <label className="checkbox-field">
                <input
                  checked={form.canBeWaypoint}
                  onChange={(event) => updateField("canBeWaypoint", event.target.checked)}
                  type="checkbox"
                />
                <span>Dapat menjadi waypoint nanti</span>
              </label>

              <label className="checkbox-field">
                <input
                  checked={form.active}
                  onChange={(event) => updateField("active", event.target.checked)}
                  type="checkbox"
                />
                <span>Aktif</span>
              </label>
            </div>
          </section>

          <section className="master-form-group">
            <h3>Kontak & Catatan</h3>
            <div className="dialog-form__grid aligned-form-grid">
              <label>
                <span>Kontak</span>
                <input
                  onChange={(event) => updateField("contactName", event.target.value)}
                  value={form.contactName}
                />
              </label>

              <label>
                <span>Telepon</span>
                <input
                  onChange={(event) => updateField("contactPhone", event.target.value)}
                  value={form.contactPhone}
                />
              </label>

              <label>
                <span>Jam Buka</span>
                <input
                  onChange={(event) => updateField("openingHours", event.target.value)}
                  value={form.openingHours}
                />
              </label>

              <label className="dialog-form__field--wide">
                <span>Catatan</span>
                <textarea
                  onChange={(event) => updateField("notes", event.target.value)}
                  rows={3}
                  value={form.notes}
                />
              </label>
            </div>
          </section>
        </div>

        <footer className="dialog-card__actions">
          <button
            className="secondary-button"
            disabled={busy}
            onClick={onCancel}
            type="button"
          >
            Batal
          </button>

          <button className="primary-button" disabled={busy} type="submit">
            {busy ? "Menyimpan…" : "Simpan POI"}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}

function Pagination({ loading, pagination, setPage }) {
  return (
    <footer className="master-pagination">
      <span>
        {pagination.rowCount
          ? `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(
              pagination.page * pagination.pageSize,
              pagination.rowCount,
            )} dari ${pagination.rowCount}`
          : "0 data"}
      </span>

      <div>
        <button
          disabled={loading || pagination.page <= 1}
          onClick={() => setPage(pagination.page - 1)}
          type="button"
        >
          Sebelumnya
        </button>

        <button
          disabled={loading || pagination.page >= pagination.pageCount}
          onClick={() => setPage(pagination.page + 1)}
          type="button"
        >
          Berikutnya
        </button>
      </div>
    </footer>
  );
}

export function PoisTab() {
  const [pois, setPois] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    validCoordinates: 0,
    showOnMap: 0,
    activeCategories: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    rowCount: 0,
    pageCount: 1,
  });
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    activeStatus: "",
    coordinateStatus: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [formMode, setFormMode] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { sort, setSort } = useTableSort("poiName");
  const pageSize = pagination.pageSize;

  const categoryByCode = useMemo(
    () =>
      new Map(
        categories.map((category) => [
          category.value,
          category,
        ]),
      ),
    [categories],
  );

  const loadPois = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError("");

      try {
        const payload = await getMapPois({
          ...filters,
          page,
          pageSize,
          sortBy: sort.key,
          sortDir: sort.direction,
        });

        setPois(payload.pois || []);
        setCategories(payload.categories || []);
        setSummary(
          payload.summary || {
            total: 0,
            active: 0,
            validCoordinates: 0,
            showOnMap: 0,
            activeCategories: 0,
          },
        );
        setPagination(
          payload.pagination || {
            page,
            pageSize,
            rowCount: 0,
            pageCount: 1,
          },
        );
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, sort],
  );

  useEffect(() => {
    loadPois(1);
  }, [filters, loadPois]);

  async function savePoi(payload) {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response =
        formMode?.type === "edit"
          ? await updateMapPoi(formMode.poi.id, payload)
          : await createMapPoi(payload);

      setMessage(response.message);
      setFormMode(null);
      await loadPois(formMode?.type === "edit" ? pagination.page : 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function togglePoi(poi) {
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await updateMapPoi(poi.id, {
        ...poi,
        active: !poi.active,
      });
      setMessage(response.message);
      await loadPois(pagination.page);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  async function removePoi() {
    if (!deleteTarget) return;

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await deleteMapPoi(deleteTarget.id);
      setMessage(response.message);
      setDeleteTarget(null);
      await loadPois(pagination.page);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  function submitFilters(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    setFilters({
      search: String(formData.get("search") || ""),
      category: String(formData.get("category") || ""),
      activeStatus: String(formData.get("activeStatus") || ""),
      coordinateStatus: String(formData.get("coordinateStatus") || ""),
    });
  }

  function clearFilters() {
    setFilters({
      search: "",
      category: "",
      activeStatus: "",
      coordinateStatus: "",
    });
  }

  function toggleSort(column) {
    const nextSort = getNextTableSort(sort, column);
    setSort(nextSort);
    setPagination((current) => ({ ...current, page: 1 }));
  }

  function changePageSize(nextPageSize) {
    setPagination((current) => ({ ...current, page: 1, pageSize: nextPageSize }));
  }

  return (
    <DataMasterSection
      actions={
        <button
          className="primary-button"
          onClick={() =>
            setFormMode({
              type: "create",
              poi: EMPTY_FORM,
            })
          }
          type="button"
        >
          Tambah POI
        </button>
      }
      className="poi-master"
      error={error}
      message={message}
      subtitle="Kelola titik pendukung perjalanan yang dapat ditampilkan pada peta operasional."
      title="POI"
    >
      <DataMasterKpiStrip label="Ringkasan titik operasional">
        <DataMasterKpiCard
          helper="Seluruh POI tersimpan"
          icon="database"
          label="Total POI"
          value={summary.total}
        />
        <DataMasterKpiCard
          helper="Tersedia untuk operasional"
          icon="check"
          label="POI aktif"
          tone="success"
          value={summary.active}
        />
        <DataMasterKpiCard
          helper="Aktif dan ditampilkan"
          icon="location"
          label="Tampil di peta"
          tone="info"
          value={summary.showOnMap}
        />
        <DataMasterKpiCard
          helper="Siap dipetakan"
          icon="route"
          label="Koordinat valid"
          tone="success"
          value={summary.validCoordinates}
        />
        <DataMasterKpiCard
          helper="Kategori aktif digunakan"
          icon="master"
          label="Kategori"
          value={summary.activeCategories}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="poi-filter-bar dm-location-filter-bar" onSubmit={submitFilters}>
        <Icon name="search" size={15} />

        <input
          key={`search-${filters.search}`}
          defaultValue={filters.search}
          name="search"
          placeholder="Cari kode, nama, alamat, atau catatan"
        />

        <select key={`category-${filters.category}`} defaultValue={filters.category} name="category">
          <option value="">Semua kategori</option>
          {categories.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>

        <select key={`status-${filters.activeStatus}`} defaultValue={filters.activeStatus} name="activeStatus">
          <option value="">Semua status data</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
        </select>

        <select key={`coordinate-${filters.coordinateStatus}`} defaultValue={filters.coordinateStatus} name="coordinateStatus">
          <option value="">Semua status koordinat</option>
          <option value="VALID">Koordinat valid</option>
          <option value="MISSING">Koordinat belum tersedia</option>
        </select>

        <button className="secondary-button" disabled={loading} type="submit">
          Terapkan
        </button>

        <button className="text-button" disabled={loading} onClick={clearFilters} type="button">
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Kategori",
            value: categoryByCode.get(filters.category)?.label || "",
          },
          {
            label: "Status",
            value: { ACTIVE: "Aktif", INACTIVE: "Nonaktif" }[filters.activeStatus] || "",
          },
          {
            label: "Koordinat",
            value: { VALID: "Valid", MISSING: "Belum tersedia" }[filters.coordinateStatus] || "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--pois"
        emptyText="Belum ada POI yang sesuai filter."
        loading={loading}
        mode="compact"
        rowCount={pois.length}
      >
        <table className="master-table master-table--pois-compact">
          <thead>
            <tr>
              <SortableHeader column="poiName" sort={sort} toggleSort={toggleSort}>POI</SortableHeader>
              <SortableHeader column="poiCategory" sort={sort} toggleSort={toggleSort}>Kategori</SortableHeader>
              <SortableHeader column="addressText" sort={sort} toggleSort={toggleSort}>Lokasi</SortableHeader>
              <SortableHeader column="showOnMap" sort={sort} toggleSort={toggleSort}>Kesiapan peta</SortableHeader>
              <SortableHeader column="active" sort={sort} toggleSort={toggleSort}>Status</SortableHeader>
              <SortableHeader column="updatedAt" sort={sort} toggleSort={toggleSort}>Diperbarui</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>

          <tbody>
            {pois.map((poi) => {
              const category = categoryByCode.get(poi.poiCategory);

              return (
                <tr key={poi.id}>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{poi.poiName}</strong>
                      <span>{poi.poiCode}</span>
                      {(poi.contactName || poi.contactPhone) && (
                        <small>{[poi.contactName, poi.contactPhone].filter(Boolean).join(" · ")}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className="poi-category-chip"
                      style={{
                        "--poi-color": poi.markerColor || category?.markerColor,
                      }}
                    >
                      {category?.label || poi.poiCategoryLabel || poi.poiCategory}
                    </span>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <strong>{poi.addressText || "Alamat belum dilengkapi"}</strong>
                      <span>{formatCoordinate(poi.latitude, poi.longitude)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="dm-cell-stack">
                      <BooleanBadge enabled={poi.showOnMap} yes="Tampil di peta" no="Tidak ditampilkan" />
                      <span>{poi.coordinateStatus === "VALID" ? "Koordinat valid" : "Koordinat belum tersedia"}</span>
                    </div>
                  </td>
                  <td><ActiveBadge active={poi.active} /></td>
                  <td>{formatTimestamp(poi.updatedAt)}</td>
                  <td className="sticky-action-cell master-action-cell">
                    <DataMasterRowActions
                      active={poi.active}
                      busy={saving}
                      onDelete={() => setDeleteTarget(poi)}
                      onEdit={() =>
                        setFormMode({
                          type: "edit",
                          poi: mapPoiToForm(poi),
                        })
                      }
                      onToggle={() => togglePoi(poi)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={loadPois}
        onPageSizeChange={changePageSize}
        pagination={pagination}
      />

      {formMode && (
        <PoiForm
          busy={saving}
          categories={categories}
          initial={formMode.poi}
          onCancel={() => setFormMode(null)}
          onSubmit={savePoi}
          title={formMode.type === "edit" ? "Edit POI" : "Tambah POI"}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          busy={saving}
          description={`Hapus POI ${deleteTarget.poiName}?`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={removePoi}
          title="Hapus POI"
        />
      )}
    </DataMasterSection>
  );
}
